<script setup lang="ts">
// Shared catalog results view: filter mask + selected chips + list cards (mobile + desktop).
// Used route-driven on the Overview page (no objPath) and inside the price-list catalog modal
// (objPath set → local psx, no navigation, self-loads mask + list; noLink keeps the cards from
// navigating). In the modal the cards' CartIcons mark the modal's selection (provided by
// CartCatalogModal) — where the picked series go is the modal's business, not this view's.
import type * as pf from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import { ut_buildParamData, ut_getText, ut_isPrint, ut_isScreenSize } from '@base/utils/utils'
import { ECarts, EScreenSize } from '@base/utils/constants'
import { useMask } from '@base/composables/mask'
import { useAppIndexStore } from '@base/stores/indexStore'
import { useListStore } from '@base/stores/listStore'
import { ECartIconAr, ECartPageText } from '~/utils/constants'
import { priceListSelectionKey } from '~/composables/priceListCart'
import { ut_wilo_filterByText } from '~/utils/utils'

const p = defineProps<{
  realCountry: string
  countryLanguage: string
  isHistoric: boolean
  isInternal: boolean
  isSeries: boolean
  isApplication?: boolean
  isMotors?: boolean
  showKoreanTeaser?: boolean
  objPath?: string // set → modal mode: local psx, no navigation, self-loads
  noLink?: boolean // cards don't navigate (modal)
  noDependentFilters?: boolean // modal: leave out the filters a selection reveals (see MaskContent)
}>()

// Modal only: "apply selection". The parent owns what it means — this view just says it was pressed.
const emit = defineEmits<{
  save: []
}>()

const indexStore = useAppIndexStore()
// Modal only: the selection the modal collects (see CartCatalogModal) — absent on the Overview page.
const selection = inject(priceListSelectionKey, undefined)
const localPsx: Ref<string> = ref('')
const isModal: ComputedRef<boolean> = computed(() => !!p.objPath)

// Mask instance for the result count / selected chips. Modal mode reads the fixed objPath + local
// psx; route mode (no objPath) reads the current route node and psx.
const maskArgs = reactive({
  objPath: computed(() => p.objPath),
  noUrlParams: computed(() => isModal.value),
  psx: localPsx,
})
const cpMask = useMask(maskArgs, (_e, v) => { localPsx.value = v })

const filterVisible: Ref<boolean> = ref(ut_isScreenSize([EScreenSize.sc_md, EScreenSize.sc_lg, EScreenSize.sc_none, EScreenSize.sc_n_a]))
watch(() => indexStore.state.screenSize, () => {
  filterVisible.value = ut_isScreenSize([EScreenSize.sc_md, EScreenSize.sc_lg, EScreenSize.sc_none, EScreenSize.sc_n_a])
})

// "Add all shown" and "apply selection" exist where there is a selection to fill — the price-list
// catalog modal. The button looks like the cart it fills (price-list icon + page label).
const hasSelection: boolean = !!selection
const addAllIcon: string = 'i-wilo-' + (ECartIconAr[ECarts.ct_price_list_create] || '')
const addAllLabel: string = ut_getText('txt_add-all-to-' + ECartPageText[ECarts.ct_price_list_create])

/*
 * Modal only: the text search (24.09.2026). It narrows what the mask's filters left — on TOP of them,
 * never instead — by the series name, and it suggests from exactly those series. Zero hits is a valid
 * answer and gets no message of its own (Christoph). The list below loads the whole filtered set
 * (useList, noUrlParams), so the search sees every series and not a first page.
 */
const textFilter: Ref<string> = ref('')

/*
 * What "add all shown", the result count and the suggestions act on: exactly the series the list below
 * is showing, mask filters AND text search included — so "all shown" means what it says on screen and
 * not "everything in this catalog".
 *
 * The list is read from the store under the same key the child list computes, built with the shared
 * helper so the two cannot drift apart; the text goes through the same function the tiles use.
 */
function shownNodes(): pf.TPfNode[] {
  if (!p.objPath) { return [] }
  const param: tp.IParamData = ut_buildParamData(indexStore.state.paramData, { objPath: p.objPath, psx: localPsx.value, sort: '' })
  return ut_wilo_filterByText(useListStore().getListData(param)?.nodes || [], textFilter.value)
}

// The count next to the results: the mask's own total, or — while a text narrows it — the series left.
function shownCount(): number {
  return isModal.value && textFilter.value.trim() ? shownNodes().length : cpMask.totalCount.value
}

// Names of the shown series that contain the text — at most ten, the exact match left out (picking
// it would change nothing).
function searchSuggestions(): string[] {
  const term: string = textFilter.value.trim().toLowerCase()
  if (!term) { return [] }
  const names: string[] = [...new Set(shownNodes().map(node => node.desc || '').filter(name => !!name))]
  return names.filter(name => name.toLowerCase() !== term).slice(0, 10)
}

// Mark every shown series, in catalog order behind what is already marked. Marked ones stay as they
// are, so pressing it twice changes nothing. Nothing is written to the cart here — the modal does
// that on "accept" (CartCatalogModal).
function addAllShown(): void {
  if (!selection || !p.objPath) { return }
  selection.add(shownNodes().map(node => p.objPath + '/' + node.nodeName))
}
</script>

<template>
  <!-- Modal only: "apply selection", top right, where it stays reachable while the tile grid below
       scrolls. No Cancel button (24.09.2026) — the X, Escape and a click outside close the modal, and
       since nothing is written before this button, closing IS cancelling (see CartCatalogModal). -->
  <div v-if="hasSelection" class="flex justify-end mb-8">
    <UButton
      icon="i-wilo-chevron-right-solid-full"
      :label="ut_getText('btn_apply_selection')"
      @click="emit('save')"
    />
  </div>
  <!-- Mobile view only (number of results and show/hide filters) -->
  <div v-if="ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm])" class="block md:hidden">
    <!-- No settings in the modal (24.09.2026): country, language and the rest belong to the page the
         project sits on, not to picking series for it. -->
    <div v-if="!isModal" class="-ml-2 mb-2">
      <SettingsSmall :is-historic="isHistoric" :real-country="realCountry" :is-internal="isInternal" :show-frequency="indexStore.getIsNavigation || (indexStore.getIsConfig && !isSeries)" />
    </div>
    <div v-if="showKoreanTeaser" class="mb-4">
      <LazyLinkTeaser :rcco="indexStore.state.paramData.rcco" />
    </div>
    <!-- Modal: the same button as on the desktop row below. Duplicated for the same reason the
         result count is — the two screen sizes lay this area out differently. -->
    <div v-if="hasSelection" class="flex flex-wrap items-center gap-2 mb-4">
      <UButton :icon="addAllIcon" :label="addAllLabel" @click="addAllShown()" />
    </div>
    <div v-if="isModal" class="mb-4">
      <CatalogSearch v-model="textFilter" :suggestions="searchSuggestions()" />
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
      <div class="mr-auto font-bold" v-html="ut_getText('txt_productsfound', [' [' + shownCount() + ']'])" />
      <UButton
        v-if="cpMask.maskData.value?.nodes?.length"
        class="sm:ml-auto"
        :icon="filterVisible ? 'i-wilo-xmark-solid-full' : 'i-wilo-filter-solid-full'"
        :label="ut_getText(filterVisible ? 'txt_hide_filter' : 'txt_show_filter')"
        color="tertiary"
        variant="ghost"
        @click="void (filterVisible = !filterVisible)"
      />
    </div>
  </div>

  <!-- Mask, MaskSelected, List -->
  <div class="grid md:grid-cols-3 lg:grid-cols-4 gap-8">
    <UCollapsible v-if="!ut_isPrint() && cpMask.maskData.value?.nodes?.length" v-model:open="filterVisible">
      <template #content>
        <LazyMask
          :obj-path="objPath"
          :no-url-params="isModal"
          :psx="localPsx"
          :country-language="countryLanguage"
          :real-country="realCountry"
          :is-historic="isHistoric"
          :is-series="false"
          :no-zero="true"
          :no-dependent-filters="noDependentFilters"
          @update:psx="localPsx = $event"
        />
      </template>
    </UCollapsible>
    <div class="col-span-1" :class="cpMask.maskData.value?.nodes?.length ? 'md:col-span-2 lg:col-span-3' : 'md:col-span-3 lg:col-span-4'">
      <!-- Desktop view (number of results) -->
      <div class="pt-1 mb-4 font-bold hidden md:flex justify-between items-center">
        <!-- Modal: "add all shown" sits directly behind the result count, because that count is what
             it acts on — "all shown" is the number standing right next to it. -->
        <div class="flex items-center gap-2">
          <div v-html="ut_getText('txt_productsfound', [' [' + shownCount() + ']'])" />
          <template v-if="hasSelection">
            <UButton class="ml-4" :icon="addAllIcon" :label="addAllLabel" @click="addAllShown()" />
          </template>
        </div>
        <LazyLinkTeaser v-if="showKoreanTeaser" :rcco="indexStore.state.paramData.rcco" />
        <div v-if="!isModal" class="-mr-2">
          <SettingsSmall :is-historic="isHistoric" :real-country="realCountry" :is-internal="isInternal" :show-frequency="indexStore.getIsNavigation || (indexStore.getIsConfig && !isSeries)" />
        </div>
        <!-- Modal: the text search, where the settings stand on the page. -->
        <CatalogSearch v-else v-model="textFilter" :suggestions="searchSuggestions()" />
      </div>

      <!-- Selected filters -->
      <LazyMaskSelected
        v-if="cpMask.maskData.value?.nodes?.length && cpMask.selected.value.length"
        class="my-8"
        :selected="cpMask.selected.value"
        :mask-data="cpMask.maskData.value"
        @update:remove="cpMask.setSelected(false, cpMask.selected.value, undefined, undefined, $event)"
        @update:remove-all="cpMask.removeAll(cpMask.selected.value)"
      />

      <LazyList
        :obj-path="objPath"
        :no-url-params="isModal"
        :psx="localPsx"
        :use-cards="true"
        :country-language="countryLanguage"
        :real-country="realCountry"
        :is-historic="isHistoric"
        :no-filters="!cpMask.maskData.value?.nodes?.length"
        :is-application="isApplication"
        :is-motors="isMotors"
        :is-internal="isInternal"
        :no-link="noLink"
        :text-filter="isModal ? textFilter : ''"
      />
    </div>
  </div>
</template>
